import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const ROLE_COLORS = {
  manager: '#818cf8', organizer: '#f97316', creator: '#34d399',
  reviewer: '#fbbf24', final_approver: '#a78bfa',
  distributor: '#60a5fa', learner: '#f472b6',
};

const STATUS_COLORS = {
  draft: '#64748b', in_review: '#f59e0b',
  changes_requested: '#ef4444', final_review: '#8b5cf6',
  approved: '#10b981', deployed: '#3b82f6',
};

const STATUS_LABELS = {
  draft: 'Draft', in_review: 'In Review',
  changes_requested: 'Changes Requested', final_review: 'Final Review',
  approved: 'Approved', deployed: 'Deployed',
};

const NAV_ITEMS = [
  { id: 'dashboard', icon: '⬡', label: 'Dashboard' },
  { id: 'users', icon: '◈', label: 'Users' },
  { id: 'roles', icon: '◉', label: 'Roles' },
  { id: 'backlogs', icon: '◫', label: 'Backlogs' },
  { id: 'assignments', icon: '◧', label: 'Assignments' },
  { id: 'organizations', icon: '◬', label: 'Organizations' },
];

const styles = {
  root: {
    display: 'flex', height: '100%', overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: '#020617',
    backgroundImage: `
      linear-gradient(rgba(0,255,157,0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,255,157,0.05) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    color: '#f8fafc',
  },
  sidebar: {
    width: '200px',
    background: 'rgba(10,18,38,0.97)',
    borderRight: '1px solid rgba(0,255,157,0.08)',
    display: 'flex', flexDirection: 'column',
    padding: '24px 12px 16px',
    gap: '2px',
    zIndex: 10,
    flexShrink: 0,
    backdropFilter: 'blur(12px)',
  },
  sidebarLogo: {
    width: '36px', height: '36px', borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(0,255,157,0.2), rgba(0,255,157,0.05))',
    border: '1px solid rgba(0,255,157,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', fontWeight: 900, color: '#00ff9d',
    marginBottom: '20px', letterSpacing: '-1px',
    boxShadow: '0 4px 12px rgba(0,255,157,0.2)',
  },
  navBtn: (active) => ({
    width: '40px', height: '40px', borderRadius: '10px',
    border: active ? '1px solid rgba(0,255,157,0.3)' : '1px solid transparent',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', transition: 'all 0.15s',
    background: active ? 'rgba(0,255,157,0.1)' : 'transparent',
    color: active ? '#00ff9d' : '#475569',
    position: 'relative',
  }),
  main: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    background: 'transparent',
  },
  topbar: {
    height: '56px', borderBottom: '1px solid rgba(0,255,157,0.08)',
    display: 'flex', alignItems: 'center', padding: '0 28px',
    gap: '12px', flexShrink: 0,
    background: 'rgba(2,6,23,0.8)',
    backdropFilter: 'blur(12px)',
  },
  content: {
    flex: 1, overflowY: 'auto', padding: '28px',
  },
  card: {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(0,255,157,0.1)',
    borderRadius: '12px', padding: '20px',
    backdropFilter: 'blur(8px)',
  },
  badge: (color) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 10px', borderRadius: '20px',
    fontSize: '11px', fontWeight: 700,
    background: color + '18', color: color,
    border: `1px solid ${color}30`,
  }),
  input: {
    width: '100%', padding: '9px 12px',
    background: 'rgba(0,255,157,0.04)',
    border: '1px solid rgba(0,255,157,0.15)',
    borderRadius: '8px', color: '#f8fafc',
    fontSize: '13px', outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  btn: (variant = 'primary') => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none',
    fontWeight: 600, fontSize: '13px', cursor: 'pointer',
    transition: 'all 0.15s',
    ...(variant === 'primary' ? {
      background: 'linear-gradient(135deg, rgba(0,255,157,0.2), rgba(0,255,157,0.08))',
      color: '#00ff9d',
      border: '1px solid rgba(0,255,157,0.35)',
      boxShadow: '0 4px 12px rgba(0,255,157,0.15)',
    } : variant === 'ghost' ? {
      background: 'rgba(255,255,255,0.04)',
      color: '#94a3b8',
      border: '1px solid rgba(255,255,255,0.08)',
    } : variant === 'danger' ? {
      background: 'rgba(239,68,68,0.1)',
      color: '#f87171',
      border: '1px solid rgba(239,68,68,0.2)',
    } : {}),
  }),
  avatar: (color = '#00ff9d') => ({
    width: '32px', height: '32px', borderRadius: '8px',
    background: color + '18', color: color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 700, flexShrink: 0,
    border: `1px solid ${color}30`,
  }),
  listItem: (selected) => ({
    padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
    border: `1px solid ${selected ? 'rgba(0,255,157,0.3)' : 'rgba(255,255,255,0.06)'}`,
    background: selected ? 'rgba(0,255,157,0.06)' : 'rgba(255,255,255,0.02)',
    transition: 'all 0.15s',
    marginBottom: '6px',
  }),
  sectionTitle: {
    fontSize: '20px', fontWeight: 700, color: '#f1f5f9',
    margin: '0 0 4px', letterSpacing: '-0.3px',
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  sectionSub: {
    fontSize: '13px', color: '#475569', margin: '0 0 24px',
  },
  label: {
    fontSize: '11px', fontWeight: 600, color: '#00ff9d',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    display: 'block', marginBottom: '6px',
    opacity: 0.7,
  },
};

function Tooltip({ label }) {
  return (
    <div style={{
      position: 'absolute', left: '52px', top: '50%', transform: 'translateY(-50%)',
      background: 'rgba(15,23,42,0.95)',
      border: '1px solid rgba(0,255,157,0.2)',
      borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600,
      color: '#00ff9d', whiteSpace: 'nowrap', pointerEvents: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)', zIndex: 100,
    }}>
      {label}
    </div>
  );
}

function NavButton({ item, active, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={item.label}
      style={{
        width: '100%',
        padding: '9px 14px',
        borderRadius: '8px',
        border: active ? '1px solid rgba(0,255,157,0.2)' : '1px solid transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px',
        fontWeight: active ? 700 : 500,
        transition: 'all 0.15s',
        background: active
          ? 'linear-gradient(135deg, rgba(0,255,157,0.12), rgba(0,255,157,0.04))'
          : hover ? 'rgba(255,255,255,0.04)' : 'transparent',
        color: active ? '#00ff9d' : hover ? '#cbd5e1' : '#64748b',
        textAlign: 'left',
        letterSpacing: '0.01em',
        boxShadow: active ? '0 2px 8px rgba(0,255,157,0.08)' : 'none',
      }}
    >
      {active && (
        <span style={{
          width: '3px', height: '14px', borderRadius: '2px',
          background: '#00ff9d',
          boxShadow: '0 0 6px rgba(0,255,157,0.6)',
          flexShrink: 0,
        }} />
      )}
      {item.label}
    </button>
  );
}

// ── Main AdminPage ────────────────────────────────────────────────────────────
export default function AdminPage({ token, userRole, onExit }) {
  const isOrganizer = userRole === 'organizer';
  const { logout } = useAuth0();
  const [activeSection, setActiveSection] = useState(isOrganizer ? 'users' : 'dashboard');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [myOrg, setMyOrg] = useState(null);

  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchRoles = useCallback(async () => {
    const res = await fetch(`${API_URL}/admin/roles`, { headers });
    const data = await res.json();
    if (data.success) setRoles(data.roles);
  }, [token]);

  const fetchUsers = useCallback(async () => {
    const res = await fetch(`${API_URL}/admin/users`, { headers });
    const data = await res.json();
    if (data.success) setUsers(data.users);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    const fetches = [fetchRoles(), fetchUsers()];
    if (isOrganizer) {
      fetches.push(
        fetch(`${API_URL}/admin/organizations`, { headers })
          .then(r => r.json())
          .then(d => { const orgs = d.organizations || []; if (orgs.length > 0) setMyOrg(orgs[0]); })
          .catch(() => {})
      );
    }
    Promise.all(fetches).finally(() => setLoading(false));
  }, [fetchRoles, fetchUsers]);

  const fetchUserRoles = async (userId) => {
    const res = await fetch(`${API_URL}/admin/users/${userId}/roles`, { headers });
    const data = await res.json();
    if (data.success) setUserRoles(data.roles.map(r => r.id));
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    await fetchUserRoles(user.user_id);
  };

  const handleToggleUserRole = async (roleId) => {
    if (!selectedUser) return;
    const hasRole = userRoles.includes(roleId);
    try {
      if (hasRole) {
        await fetch(`${API_URL}/admin/users/${selectedUser.user_id}/roles`, {
          method: 'DELETE', headers, body: JSON.stringify({ role_ids: [roleId] }),
        });
        setUserRoles(prev => prev.filter(r => r !== roleId));
      } else {
        await fetch(`${API_URL}/admin/users/${selectedUser.user_id}/roles`, {
          method: 'POST', headers, body: JSON.stringify({ role_ids: [roleId] }),
        });
        setUserRoles(prev => [...prev, roleId]);
      }
    } catch (e) {
      console.warn('Role toggle failed, retrying...', e);
      setTimeout(() => handleToggleUserRole(roleId), 500);
    }
  };

  const handleSaveRole = async () => {
    setSaving(true); setError('');
    try {
      if (editingRole) {
        await fetch(`${API_URL}/admin/roles/${editingRole.id}`, {
          method: 'PATCH', headers, body: JSON.stringify({ name: roleName, description: roleDesc }),
        });
      } else {
        await fetch(`${API_URL}/admin/roles`, {
          method: 'POST', headers, body: JSON.stringify({ name: roleName, description: roleDesc }),
        });
      }
      await fetchRoles();
      setShowRoleForm(false); setEditingRole(null); setRoleName(''); setRoleDesc('');
    } catch { setError('Failed to save role'); }
    finally { setSaving(false); }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    await fetch(`${API_URL}/admin/roles/${roleId}`, { method: 'DELETE', headers });
    await fetchRoles();
  };

  const handleEditRole = (role) => {
    setEditingRole(role); setRoleName(role.name); setRoleDesc(role.description);
    setShowRoleForm(true);
  };

  const activeItem = NAV_ITEMS.find(n => n.id === activeSection);

  return (
    <div style={styles.root}>
      {/* ── Sidebar ── */}
      <div style={styles.sidebar}>
        
        {NAV_ITEMS.filter(item => {
          if (isOrganizer) return ['users', 'backlogs', 'organizations'].includes(item.id);
          return true;
        }).map(item => (
          <NavButton key={item.id} item={item}
            active={activeSection === item.id}
            onClick={() => setActiveSection(item.id)} />
        ))}
        <div style={{ flex: 1 }} />
        
        <div style={{ flex: 1 }} />
        <div style={{
          height: '1px', background: 'rgba(0,255,157,0.08)',
          margin: '8px 0',
        }} />
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          style={{
            width: '100%', padding: '9px 14px', borderRadius: '8px',
            border: '1px solid transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '13px', fontWeight: 500,
            background: 'transparent', color: '#475569',
            textAlign: 'left', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = 'transparent'; }}
        >
          Sign Out
        </button>
      </div>

      {/* ── Main ── */}
      <div style={styles.main}>
        {/* Topbar */}
        {/* Topbar */}
        <div style={styles.topbar}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#00ff9d', textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7 }}>
              Admin Console
            </span>
            {isOrganizer && myOrg && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#fb923c' }}>{myOrg.name}</span>
                <span style={{ fontSize: '10px', color: '#475569', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '4px', padding: '1px 6px' }}>
                  ID: {myOrg.org_id}
                </span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '5px 12px', borderRadius: '8px',
              background: isOrganizer ? 'rgba(249,115,22,0.1)' : 'rgba(99,102,241,0.1)',
              border: isOrganizer ? '1px solid rgba(249,115,22,0.25)' : '1px solid rgba(99,102,241,0.25)',
            }}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '6px',
                background: isOrganizer ? 'linear-gradient(135deg, #f97316, #fb923c)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', color: '#fff', fontWeight: 700,
              }}>{isOrganizer ? 'O' : 'M'}</div>
              <span style={{ fontSize: '12px', color: isOrganizer ? '#fb923c' : '#a5b4fc', fontWeight: 600 }}>{isOrganizer ? 'Organizer' : 'Manager'}</span>
            </div>
            {onExit && (
              <button
                onClick={onExit}
                title="Close Admin Panel"
                style={{
                  width: '28px', height: '28px', borderRadius: '8px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#f87171', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', fontWeight: 700,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(239,68,68,0.25)';
                  e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)';
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
              color: '#f87171', padding: '10px 16px', borderRadius: '8px', marginBottom: '20px',
              fontSize: '13px',
            }}>{error}</div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', paddingTop: '80px', color: 'white' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>◉</div>
              <div style={{ fontSize: '13px' }}>Loading workspace...</div>
            </div>
          ) : (
            <>
              {activeSection === 'dashboard' && <ManagerDashboard token={token} users={users} API_URL={API_URL} />}
              {activeSection === 'users' && (
                <UsersSection
                  users={users} roles={roles} selectedUser={selectedUser}
                  userRoles={userRoles} onSelectUser={handleSelectUser}
                  onToggleRole={handleToggleUserRole} token={token}
                  isOrganizer={isOrganizer}
                />
              )}
              {activeSection === 'roles' && (
                <RolesSection
                  roles={roles} showForm={showRoleForm} editingRole={editingRole}
                  roleName={roleName} roleDesc={roleDesc} saving={saving}
                  onNew={() => { setShowRoleForm(true); setEditingRole(null); setRoleName(''); setRoleDesc(''); }}
                  onEdit={handleEditRole} onDelete={handleDeleteRole}
                  onSave={handleSaveRole} onCancel={() => { setShowRoleForm(false); setEditingRole(null); }}
                  setRoleName={setRoleName} setRoleDesc={setRoleDesc}
                />
              )}
              {activeSection === 'backlogs' && <BacklogManager token={token} users={users} roles={roles} API_URL={API_URL} isOrganizer={isOrganizer} myOrg={myOrg} />}
              {activeSection === 'assignments' && <LessonAssignments token={token} roles={roles} users={users} API_URL={API_URL} />}
              {activeSection === 'organizations' && <OrganizationsSection token={token} users={users} API_URL={API_URL} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Users Section ─────────────────────────────────────────────────────────────
function UsersSection({ users, roles, selectedUser, userRoles, onSelectUser, onToggleRole, token, isOrganizer }) {
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importError, setImportError] = useState(null);
  const [orgMemberIds, setOrgMemberIds] = useState(null); // null = not loaded yet

  useEffect(() => {
    if (!isOrganizer) return;
    fetch(`${API_URL}/admin/organizations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const orgs = d.organizations || [];
        const members = orgs.length > 0 ? (orgs[0].members || []) : [];
        setOrgMemberIds(members);
      })
      .catch(() => setOrgMemberIds([]));
  }, [token, isOrganizer]);

  const isOrgMember = (userId) => !isOrganizer || (orgMemberIds || []).includes(userId);
  const FORBIDDEN_ROLE_NAMES = new Set(['manager', 'organizer']);

  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const rows = [];
          if (ext === 'json') {
            const data = JSON.parse(text);
            const arr = Array.isArray(data) ? data : data.users || data.rows || [];
            arr.forEach(item => {
              if (item.email && item.role) rows.push({ email: item.email, role: item.role });
            });
          } else {
            // CSV or TSV
            const sep = ext === 'tsv' ? '\t' : ',';
            const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
            if (lines.length < 2) { reject('File must have a header row and at least one data row'); return; }
            const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
            const emailIdx = headers.indexOf('email');
            const roleIdx = headers.indexOf('role');
            if (emailIdx === -1 || roleIdx === -1) { reject('CSV must have "email" and "role" columns'); return; }
            lines.slice(1).forEach(line => {
              const cols = line.split(sep).map(c => c.trim().replace(/['"]/g, ''));
              const email = cols[emailIdx];
              const role = cols[roleIdx];
              if (email && role) rows.push({ email, role });
            });
          }
          resolve(rows);
        } catch (err) { reject('Failed to parse file: ' + err.message); }
      };
      reader.onerror = () => reject('Failed to read file');
      reader.readAsText(file);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    setImportResults(null);
    setImporting(true);
    try {
      const rows = await parseFile(file);
      if (rows.length === 0) { setImportError('No valid rows found in file'); setImporting(false); return; }
      const res = await fetch(`${API_URL}/admin/users/bulk-assign-roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      setImportResults(data);
    } catch (err) {
      setImportError(err.toString());
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', gap: '20px', height: '100%' }}>
      {/* Left: user list */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={styles.sectionTitle}>Users</p>
        <p style={styles.sectionSub}>Select a user to manage their global roles</p>

        {/* Bulk import */}
        <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '10px', background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#a5b4fc', marginBottom: '6px' }}>Bulk Import Roles</div>
          <div style={{ fontSize: '11px', color: '#475569', marginBottom: '10px' }}>
            Upload CSV, TSV, or JSON. Required columns: <code style={{ color: '#94a3b8' }}>email</code>, <code style={{ color: '#94a3b8' }}>role</code>
          </div>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '7px 14px', borderRadius: '7px', cursor: importing ? 'not-allowed' : 'pointer',
            background: importing ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.2)',
            border: '1px solid rgba(99,102,241,0.3)',
            fontSize: '12px', fontWeight: 600, color: '#a5b4fc',
            transition: 'all 0.15s',
          }}>
            {importing ? '⏳ Importing...' : '⬆ Upload File'}
            <input type="file" accept=".csv,.tsv,.json" onChange={handleFileUpload} disabled={importing} style={{ display: 'none' }} />
          </label>

          {/* Results */}
          {importResults && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', color: '#34d399', fontWeight: 600, marginBottom: '6px' }}>
                ✓ {importResults.assigned}/{importResults.total} assigned successfully
              </div>
              <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {importResults.results.map((r, i) => (
                  <div key={i} style={{
                    fontSize: '11px', padding: '4px 8px', borderRadius: '5px',
                    background: r.status === 'success' ? 'rgba(52,211,153,0.08)' : 'rgba(239,68,68,0.08)',
                    color: r.status === 'success' ? '#34d399' : '#f87171',
                    display: 'flex', justifyContent: 'space-between',
                  }}>
                    <span>{r.email} → {r.role}</span>
                    <span>{r.status === 'success' ? '✓' : `✗ ${r.message}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {importError && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#f87171' }}>✗ {importError}</div>
          )}
        </div>

        {/* Existing user list */}
        <div>
          {users.map(user => {
            const isSelected = selectedUser?.user_id === user.user_id;
            const initial = (user.name || user.email || '?').charAt(0).toUpperCase();
            return (
              <div key={user.user_id} onClick={() => onSelectUser(user)} style={styles.listItem(isSelected)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={styles.avatar('#6366f1')}>{initial}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0' }}>{user.name || 'No name'}</div>
                    <div style={{ fontSize: '11px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  </div>
                  {isSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6366f1' }} />}
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#dce1e8', fontSize: '13px' }}>No users found</div>
          )}
        </div>
      </div>

      {/* Right: role assignment */}
      <div style={{ width: '300px', flexShrink: 0 }}>
        {selectedUser ? (
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div style={styles.avatar('#6366f1')}>
                {(selectedUser.name || selectedUser.email || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#e2e8f0' }}>{selectedUser.name || 'No name'}</div>
                <div style={{ fontSize: '11px', color: '#475569' }}>{selectedUser.email}</div>
              </div>
            </div>
            <span style={styles.label}>Assign Roles</span>
            {isOrganizer && !isOrgMember(selectedUser.user_id) && (
              <div style={{ fontSize: '12px', color: '#f97316', marginBottom: '10px', padding: '8px 12px', background: 'rgba(249,115,22,0.08)', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.2)' }}>
                This user is not in your organization. Add them in the Organizations tab first.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {roles.filter(role => !isOrganizer || !FORBIDDEN_ROLE_NAMES.has(role.name)).map(role => {
                const active = userRoles.includes(role.id);
                const color = ROLE_COLORS[role.name] || '#6366f1';
                const canToggle = !isOrganizer || isOrgMember(selectedUser.user_id);
                return (
                  <div key={role.id} onClick={() => canToggle && onToggleRole(role.id)}
                    style={{
                      padding: '10px 14px', borderRadius: '8px', cursor: canToggle ? 'pointer' : 'not-allowed',
                      border: `1px solid ${active ? color + '40' : 'rgba(255,255,255,0.06)'}`,
                      background: active ? color + '10' : 'rgba(255,255,255,0.02)',
                      display: 'flex', alignItems: 'center', gap: '10px',
                      transition: 'all 0.15s', opacity: canToggle ? 1 : 0.4,
                    }}>
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                      background: active ? color : 'transparent',
                      border: `2px solid ${active ? color : 'rgba(255,255,255,0.15)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      {active && <span style={{ color: '#fff', fontSize: '9px', fontWeight: 900 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: active ? color : '#94a3b8' }}>
                        {role.name}
                      </div>
                    </div>
                    {active && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ ...styles.card, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
            <div style={{ textAlign: 'center', color: '#dce1e8', fontSize: '13px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>◈</div>
              Select a user to manage roles
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// ── Roles Section ─────────────────────────────────────────────────────────────
function RolesSection({ roles, showForm, editingRole, roleName, roleDesc, saving,
  onNew, onEdit, onDelete, onSave, onCancel, setRoleName, setRoleDesc }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <p style={styles.sectionTitle}>Roles</p>
          <p style={styles.sectionSub}>Define roles and their permissions</p>
        </div>
        <button onClick={onNew} style={styles.btn('primary')}>+ New Role</button>
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: '20px', borderColor: 'rgba(99,102,241,0.3)' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#e2e8f0', marginBottom: '16px' }}>
            {editingRole ? '✏️ Edit Role' : '+ New Role'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <span style={styles.label}>Role Name</span>
              <input value={roleName} onChange={e => setRoleName(e.target.value)}
                placeholder="e.g. reviewer" style={styles.input}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
            </div>
            <div>
              <span style={styles.label}>Description</span>
              <textarea value={roleDesc} onChange={e => setRoleDesc(e.target.value)}
                placeholder="What can this role do?" rows={2}
                style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={onSave} disabled={saving || !roleName.trim()} style={styles.btn('primary')}>
                {saving ? 'Saving...' : 'Save Role'}
              </button>
              <button onClick={onCancel} style={styles.btn('ghost')}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
        {roles.map(role => {
          const color = ROLE_COLORS[role.name] || '#6366f1';
          return (
            <div key={role.id} style={{
              ...styles.card,
              borderLeft: `3px solid ${color}`,
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: color + '20', border: `1px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px',
                }}>
                  {role.name === 'manager' ? '⬡' : role.name === 'creator' ? '◈' :
                   role.name === 'reviewer' ? '◉' : role.name === 'final_approver' ? '◫' :
                   role.name === 'distributor' ? '◧' : '●'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#e2e8f0' }}>{role.name}</div>
                </div>
                <div style={styles.badge(color)}>{role.name}</div>
              </div>
              <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
                {role.description || 'No description'}
              </div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                <button onClick={() => onEdit(role)} style={{ ...styles.btn('ghost'), fontSize: '12px', padding: '5px 12px' }}>
                  Edit
                </button>
                <button onClick={() => onDelete(role.id)} style={{ ...styles.btn('danger'), fontSize: '12px', padding: '5px 12px' }}>
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Lesson Assignments ────────────────────────────────────────────────────────
function LessonAssignments({ token, roles, users, API_URL }) {
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [usersByRole, setUsersByRole] = useState({});
  const [search, setSearch] = useState('');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_URL}/lesson/templates`, { headers })
      .then(r => r.json())
      .then(data => { if (data.templates) setLessons(data.templates); });
    const assignableRoles = ['creator', 'reviewer', 'final_approver', 'distributor'];
    assignableRoles.forEach(roleName => {
      const roleObj = roles.find(r => r.name === roleName);
      if (roleObj) {
        fetch(`${API_URL}/admin/roles/${roleObj.id}/users`, { headers })
          .then(r => r.json())
          .then(data => {
            if (data.success) setUsersByRole(prev => ({ ...prev, [roleName]: data.users || [] }));
          }).catch(() => {});
      }
    });
  }, [token, roles]);

  const handleSelectLesson = async (lesson) => {
    setSelectedLesson(lesson);
    const res = await fetch(`${API_URL}/admin/lessons/${lesson.id}/assignments`, { headers });
    const data = await res.json();
    if (data.success) setAssignments(data.assignments || {});
  };

  const handleToggle = (role, userId) => {
    setAssignments(prev => {
      const current = prev[role] || [];
      const updated = current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId];
      return { ...prev, [role]: updated };
    });
  };

  const handleSave = async () => {
    if (!selectedLesson) return;
    setSaving(true);
    await fetch(`${API_URL}/admin/lessons/${selectedLesson.id}/assignments`, {
      method: 'POST', headers, body: JSON.stringify({ assigned_users: assignments }),
    });
    setSaving(false); setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const assignableRoles = ['creator', 'reviewer', 'final_approver', 'distributor'];
  const filtered = lessons.filter(l => (l.name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: 'flex', gap: '20px', height: '100%' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={styles.sectionTitle}>Lesson Assignments</p>
        <p style={styles.sectionSub}>Assign team members to specific lessons</p>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search lessons..." style={{ ...styles.input, marginBottom: '12px' }}
          onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
        <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
          {filtered.map(lesson => (
            <div key={lesson.id} onClick={() => handleSelectLesson(lesson)}
              style={styles.listItem(selectedLesson?.id === lesson.id)}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0' }}>{lesson.name}</div>
              <div style={{ fontSize: '11px', color: '#dce1e8', marginTop: '2px' }}>{lesson.id}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width: '320px', flexShrink: 0 }}>
        {selectedLesson ? (
          <div style={styles.card}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#e2e8f0', marginBottom: '4px' }}>
              {selectedLesson.name}
            </div>
            <div style={{ fontSize: '11px', color: '#dce1e8', marginBottom: '20px' }}>{selectedLesson.id}</div>
            {assignableRoles.map(role => {
              const roleUsers = usersByRole[role] || [];
              const color = ROLE_COLORS[role] || '#6366f1';
              return (
                <div key={role} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {role.replace('_', ' ')}
                    </span>
                  </div>
                  {roleUsers.length === 0 ? (
                    <div style={{ fontSize: '11px', color: '#dce1e8', padding: '6px 0' }}>No users with this role</div>
                  ) : roleUsers.map(user => {
                    const active = (assignments[role] || []).includes(user.user_id);
                    return (
                      <div key={user.user_id} onClick={() => handleToggle(role, user.user_id)}
                        style={{
                          padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                          border: `1px solid ${active ? color + '40' : 'rgba(255,255,255,0.06)'}`,
                          background: active ? color + '10' : 'rgba(255,255,255,0.02)',
                          display: 'flex', alignItems: 'center', gap: '8px',
                          marginBottom: '4px', transition: 'all 0.15s',
                        }}>
                        <div style={{
                          width: '14px', height: '14px', borderRadius: '4px', flexShrink: 0,
                          background: active ? color : 'transparent',
                          border: `2px solid ${active ? color : 'rgba(255,255,255,0.15)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && <span style={{ color: '#fff', fontSize: '8px', fontWeight: 900 }}>✓</span>}
                        </div>
                        <div style={styles.avatar(color)}>
                          {(user.name || user.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: active ? '#e2e8f0' : '#94a3b8' }}>
                            {user.name || user.email?.split('@')[0]}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <button onClick={handleSave} disabled={saving} style={{ ...styles.btn('primary'), width: '100%', marginTop: '8px' }}>
              {saving ? 'Saving...' : saveSuccess ? '✓ Saved' : 'Save Assignments'}
            </button>
          </div>
        ) : (
          <div style={{
            ...styles.card, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', height: '200px',
          }}>
            <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.2 }}>◧</div>
            <div style={{ fontSize: '13px', color: '#dce1e8' }}>Select a lesson to assign team</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Backlog Manager ────────────────────────────────────────────────────────────
function BacklogManager({ token, users, roles, API_URL, isOrganizer, myOrg }) {
  const [backlogs, setBacklogs] = useState([]);
  const [orgsList, setOrgsList] = useState([]);
  const [orgMemberIds, setOrgMemberIds] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBacklog, setEditingBacklog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceImagePreviews, setReferenceImagePreviews] = useState([]);
  const [dataFile, setDataFile] = useState(null);
  const [dataFilePath, setDataFilePath] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageInputRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const formRef = React.useRef(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState({});
  const [isAssessment, setIsAssessment] = useState(false);
  const [assessmentSource, setAssessmentSource] = useState('topic');
  const [assessmentTopic, setAssessmentTopic] = useState('');
  const [assessmentTemplateIds, setAssessmentTemplateIds] = useState('');
  const [questionCount, setQuestionCount] = useState('');
  const [usersByRole, setUsersByRole] = useState({});

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [availableLessons, setAvailableLessons] = useState([]);

  const fetchBacklogs = async () => {
    const res = await fetch(`${API_URL}/admin/backlogs`, { headers });
    const data = await res.json();
    if (data.success) setBacklogs(data.backlogs);
    setLoading(false);
  };

  const fetchLessons = async () => {
    try {
      const res = await fetch(`${API_URL}/lesson/templates`, { headers });
      const data = await res.json();
      if (data.templates) setAvailableLessons(data.templates);
    } catch (_) {}
  };

  useEffect(() => {
    fetchBacklogs(); fetchLessons();
    fetch(`${API_URL}/admin/organizations`, { headers })
      .then(r => r.json())
      .then(d => {
        const orgs = d.organizations || [];
        setOrgsList(orgs);
        if (isOrganizer) setOrgMemberIds(orgs.length > 0 ? (orgs[0].members || []) : []);
      }).catch(() => { if (isOrganizer) setOrgMemberIds([]); });
  }, [token]);
  useEffect(() => {
  const assignableRoles = ['creator', 'reviewer', 'final_approver', 'distributor'];
  assignableRoles.forEach(roleName => {
    const roleObj = roles.find(r => r.name === roleName);
    console.log(`roleName: ${roleName} → roleObj:`, roleObj); // ADD THIS
    if (roleObj) {
      fetch(`${API_URL}/admin/roles/${roleObj.id}/users`, { headers })
        .then(r => r.json())
        .then(data => {
          console.log(`users for ${roleName}:`, data); // ADD THIS
          if (data.success) setUsersByRole(prev => ({ ...prev, [roleName]: data.users || [] }));
        }).catch(() => {});
    }
  });
}, [token, roles]);

  const resetForm = () => {
  setTitle(''); setDescription(''); setDueDate('');
  setPriority('medium'); setAssignedTo({}); setEditingBacklog(null);
  setReferenceImages([]); setReferenceImagePreviews([]);
  setDataFile(null); setDataFilePath('');
  setIsAssessment(false); setAssessmentSource('topic');
  setAssessmentTopic(''); setAssessmentTemplateIds(''); setQuestionCount('');
};

  const handleEdit = (b) => {
  setEditingBacklog(b);
  setTitle(b.title);
  setDescription(b.description || '');
  setDueDate(b.due_date || '');
  setPriority(b.priority || 'medium');
  setAssignedTo(b.assigned_to || {});
  setIsAssessment(b.is_assessment || false);
  setAssessmentSource(b.assessment_source || 'topic');
  setAssessmentTopic(b.assessment_topic || '');
  setAssessmentTemplateIds((b.assessment_template_ids || []).join(', '));
  setQuestionCount(b.question_count ? String(b.question_count) : '');
  setShowForm(true);
  setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
};

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const body = {
  title, description, due_date: dueDate, priority,
  assigned_to: assignedTo, data_file_path: dataFilePath,
  reference_image_previews: referenceImagePreviews,
  is_assessment: isAssessment,
  assessment_source: assessmentSource,
  assessment_topic: assessmentTopic,
  assessment_template_ids: assessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean),
  question_count: questionCount ? parseInt(questionCount) : null,
};
    if (editingBacklog) {
      await fetch(`${API_URL}/admin/backlogs/${editingBacklog.backlog_id}`, { method: 'PATCH', headers, body: JSON.stringify(body) });
    } else {
      await fetch(`${API_URL}/admin/backlogs`, { method: 'POST', headers, body: JSON.stringify(body) });
    }
    await fetchBacklogs(); setSaving(false); setShowForm(false); resetForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this backlog?')) return;
    await fetch(`${API_URL}/admin/backlogs/${id}`, { method: 'DELETE', headers });
    await fetchBacklogs();
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (referenceImages.length + files.length > 6) { alert('Max 6 images'); return; }
    const previews = await Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setReferenceImages(prev => [...prev, ...files].slice(0, 6));
    setReferenceImagePreviews(prev => [...prev, ...previews].slice(0, 6));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['csv','xlsx','xls','pdf','docx','pptx'].includes(ext)) { alert('Unsupported file type'); return; }
    setDataFile(file); setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API_URL}/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) setDataFilePath(data.file_path); else alert('Upload failed');
    } catch { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  const assignableRoles = ['creator', 'reviewer', 'final_approver', 'distributor'];
  const PRIORITY_COLORS = { low: '#34d399', medium: '#fbbf24', high: '#f87171' };
  const STATUS_COLORS_B = { open: '#818cf8', in_progress: '#fbbf24', done: '#34d399' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <p style={styles.sectionTitle}>Backlogs</p>
          <p style={styles.sectionSub}>Create and assign lesson production tasks</p>
        </div>
        <button onClick={() => { setShowForm(true); resetForm(); }} style={styles.btn('primary')}>
          + New Backlog
        </button>
      </div>

      {showForm && (
        <div ref={formRef} style={{ ...styles.card, marginBottom: '20px', borderColor: 'rgba(99,102,241,0.3)' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#e2e8f0', marginBottom: '16px' }}>
            {editingBacklog ? '✏️ Edit Backlog' : '+ New Backlog'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <span style={styles.label}>Title *</span>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Lesson title..."
                style={styles.input}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
            </div>
            {/* ── Assessment toggle — FIRST ── */}
            <div style={{
              background: isAssessment ? 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(251,191,36,0.06) 100%)' : 'rgba(251,191,36,0.05)',
              border: isAssessment ? '1.5px solid rgba(99,102,241,0.3)' : '1px solid rgba(251,191,36,0.2)',
              borderRadius: '12px', padding: '14px', transition: 'all 0.2s',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                  background: isAssessment ? '#6366f1' : 'rgba(255,255,255,0.1)',
                  border: '1.5px solid ' + (isAssessment ? '#6366f1' : 'rgba(255,255,255,0.2)'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {isAssessment && <span style={{ color: '#fff', fontSize: '12px', lineHeight: 1 }}>✓</span>}
                  <input
                    type="checkbox"
                    checked={isAssessment}
                    onChange={e => {
                      setIsAssessment(e.target.checked);
                      setAssessmentSource('topic');
                      setAssessmentTopic('');
                      setAssessmentTemplateIds('');
                      setQuestionCount('');
                    }}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: isAssessment ? '#a5b4fc' : '#fbbf24' }}>
                    📝 Assessment Only
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
                    Quiz-only lesson — no teaching slides
                  </div>
                </div>
              </label>

              {isAssessment && (
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(99,102,241,0.15)', paddingTop: '14px' }}>
                  <div style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '3px' }}>
                    <button type="button" onClick={() => setAssessmentSource('topic')} style={{
                      flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                      background: assessmentSource === 'topic' ? 'rgba(217,119,6,0.9)' : 'transparent',
                      color: assessmentSource === 'topic' ? '#fff' : '#94a3b8', transition: 'all 0.15s',
                    }}>🎯 By Topic</button>
                    <button type="button" onClick={() => setAssessmentSource('template_ids')} style={{
                      flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                      background: assessmentSource === 'template_ids' ? 'rgba(99,102,241,0.9)' : 'transparent',
                      color: assessmentSource === 'template_ids' ? '#fff' : '#94a3b8', transition: 'all 0.15s',
                    }}>📚 From Lessons</button>
                  </div>

                  {assessmentSource === 'topic' ? (
                    <input value={assessmentTopic} onChange={e => setAssessmentTopic(e.target.value)}
                      placeholder="e.g. Workplace Safety, Python Basics…"
                      style={{ ...styles.input }}
                      onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ border: '1px solid rgba(99,102,241,0.25)', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', maxHeight: '180px', overflowY: 'auto' }}>
                        {(availableLessons || []).filter(t => !t.id.startsWith('creating-') && !t.is_assessment).map(t => {
                          const selectedIds = assessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean);
                          const isSelected = selectedIds.includes(t.id);
                          return (
                            <div key={t.id} onClick={() => {
                              const next = isSelected ? selectedIds.filter(id => id !== t.id) : [...selectedIds, t.id];
                              setAssessmentTemplateIds(next.join(', '));
                            }} style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: isSelected ? 'rgba(99,102,241,0.15)' : 'transparent',
                              borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.1s',
                            }}>
                              <div style={{
                                width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                                background: isSelected ? '#6366f1' : 'rgba(255,255,255,0.1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff',
                              }}>{isSelected ? '✓' : ''}</div>
                              <span style={{ color: isSelected ? '#a5b4fc' : '#94a3b8', fontWeight: isSelected ? 600 : 400, flex: 1 }}>{String(t.name || '')}</span>
                              <span style={{ color: '#475569', fontSize: '10px' }}>{String(t.id || '')}</span>
                            </div>
                          );
                        })}
                        {(availableLessons || []).filter(t => !t.id.startsWith('creating-') && !t.is_assessment).length === 0 && (
                          <div style={{ padding: '12px', color: '#64748b', fontSize: '12px', textAlign: 'center' }}>No lessons available</div>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: '#475569' }}>Smart objectives are extracted from selected lessons to shape the questions.</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>Number of questions:</span>
                    <input type="number" min={5} max={50} value={questionCount} onChange={e => setQuestionCount(e.target.value)}
                      placeholder="auto" style={{ ...styles.input, width: '80px' }} />
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Blank = 2× objectives</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Description — only for non-assessment ── */}
            {!isAssessment && (
            <div>
              <span style={styles.label}>Description</span>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="What should this lesson cover?" rows={3}
                style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
            </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <span style={styles.label}>Due Date</span>
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                  style={{ ...styles.input, colorScheme: 'dark' }} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={styles.label}>Priority</span>
                <select value={priority} onChange={e => setPriority(e.target.value)}
                  style={{ ...styles.input, cursor: 'pointer' }}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div>
              <span style={styles.label}>Assign Team</span>
              {assignableRoles.map(role => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '120px', flexShrink: 0 }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: ROLE_COLORS[role] || '#6366f1' }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'capitalize' }}>
                      {role.replace('_', ' ')}
                    </span>
                  </div>
                  <select value={assignedTo[role] || ''} onChange={e => setAssignedTo(prev => ({ ...prev, [role]: e.target.value }))}
                    style={{ ...styles.input, cursor: 'pointer' }}>
                    <option value="">— Unassigned —</option>
                    {(usersByRole[role] || []).map(u => (<option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>))}
                  </select>
                </div>
              ))}
            </div>

            {/* Images */}
            <div>
              <span style={styles.label}>Reference Images (up to 6)</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {referenceImagePreviews.map((src, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={src} alt="" style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }} />
                    <button onClick={() => { setReferenceImages(p => p.filter((_, j) => j !== i)); setReferenceImagePreviews(p => p.filter((_, j) => j !== i)); }}
                      style={{ position: 'absolute', top: -6, right: -6, width: '16px', height: '16px', borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '10px' }}>×</button>
                  </div>
                ))}
                {referenceImages.length < 6 && (
                  <button onClick={() => imageInputRef.current?.click()}
                    style={{ width: '56px', height: '56px', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', fontSize: '18px', color: '#dce1e8' }}>+</button>
                )}
              </div>
              <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageSelect} />
            </div>

            {/* File */}
            <div>
              <span style={styles.label}>Data File (CSV/PDF/DOCX/PPTX)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{ ...styles.btn('ghost'), fontSize: '12px' }}>
                  {uploading ? 'Uploading...' : '📎 Choose File'}
                </button>
                {(dataFile || dataFilePath) && (
                  <span style={{ fontSize: '12px', color: '#34d399' }}>✓ {dataFile?.name || 'File attached'}</span>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.docx,.pptx" style={{ display: 'none' }} onChange={handleFileSelect} />
            </div>
            

            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
              <button onClick={handleSave} disabled={saving || !title.trim()} style={styles.btn('primary')}>
                {saving ? 'Saving...' : 'Save Backlog'}
              </button>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={styles.btn('ghost')}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#dce1e8', fontSize: '13px' }}>Loading...</div>
      ) : backlogs.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: '48px', color: '#dce1e8' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.2 }}>◫</div>
          <div style={{ fontSize: '13px' }}>No backlogs yet. Create one to get started.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {backlogs.map(b => (
            <div key={b.backlog_id} style={{
              ...styles.card,
              borderLeft: `3px solid ${PRIORITY_COLORS[b.priority] || '#64748b'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#e2e8f0' }}>{b.title}</span>
                    {b.is_assessment && (
                      <span style={{ ...styles.badge('#6366f1'), background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }}>
                        📝 Assessment
                      </span>
                    )}
                    <span style={styles.badge(PRIORITY_COLORS[b.priority] || '#64748b')}>{b.priority}</span>
                    <span style={styles.badge(STATUS_COLORS_B[b.status] || '#64748b')}>
                      {b.status?.replace('_', ' ')}
                    </span>
                  </div>
                  {b.description && <p style={{ margin: '0 0 8px', color: '#475569', fontSize: '13px', lineHeight: 1.5 }}>{b.description}</p>}
                  <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#dce1e8', marginBottom: '8px' }}>
                    {b.due_date && <span>📅 {b.due_date}</span>}
                    {b.lesson_id && <span>📖 {b.lesson_id}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {Object.entries(b.assigned_to || {}).map(([role, userId]) => {
                      const u = users.find(u => u.user_id === userId);
                      const color = ROLE_COLORS[role] || '#6366f1';
                      return u ? (
                        <span key={role} style={styles.badge(color)}>
                          {role.replace('_', ' ')}: {u.name || u.email?.split('@')[0]}
                        </span>
                      ) : null;
                    })}
                  </div>
                  {(b.org_id || (isOrganizer && myOrg)) && (() => {
                    const org = b.org_id ? orgsList.find(o => o.org_id === b.org_id) : myOrg;
                    if (!org) return null;
                    const organizer = users.find(u => u.user_id === org.organizer_id);
                    return (
                      <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(249,115,22,0.08)', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.2)' }}>
                        <div style={{ fontSize: '11px', color: '#fb923c', fontWeight: 600, marginBottom: '3px' }}>◬ Organization</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.6 }}>
                          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{org.name}</span>
                          {' · '}ID: <span style={{ color: '#cbd5e1' }}>{org.org_id}</span>
                          {organizer && <><br />Organization admin: <span style={{ color: '#fdba74' }}>{organizer.email || organizer.name}</span></>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => handleEdit(b)} style={{ ...styles.btn('ghost'), fontSize: '12px', padding: '5px 12px' }}>Edit</button>
                  <button onClick={() => handleDelete(b.backlog_id)} style={{ ...styles.btn('danger'), fontSize: '12px', padding: '5px 12px' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeployedTab({ deployments, deployedLessons, progress, users, token, API_URL }) {
  const [expandedId, setExpandedId] = React.useState(null);
  const [histories, setHistories] = React.useState({});
  const [loadingHistory, setLoadingHistory] = React.useState({});
  const [rollingBack, setRollingBack] = React.useState({});
  const [rollbackNote, setRollbackNote] = React.useState({});
  const [copied, setCopied] = React.useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso.slice(0, 16); }
  };

  const getUserName = (userId) => {
    if (!userId) return null;
    const u = users.find(u => u.user_id === userId);
    return u ? (u.name || u.email?.split('@')[0]) : userId?.slice(-6);
  };

  const normDep = (raw) => {
    if (!raw) return null;
    if (raw.current) return raw.current;
    return raw;
  };

  const fetchHistory = async (templateId) => {
    if (histories[templateId]) return;
    setLoadingHistory(prev => ({ ...prev, [templateId]: true }));
    try {
      const res = await fetch(`${API_URL}/lesson/templates/${templateId}/deployment/versions`, { headers });
      const data = await res.json();
      if (data.success) {
        setHistories(prev => ({ ...prev, [templateId]: data.history || [] }));
      }
    } catch (e) {
      console.warn('Failed to fetch version history:', e);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const handleExpand = (templateId) => {
    if (expandedId === templateId) {
      setExpandedId(null);
    } else {
      setExpandedId(templateId);
      fetchHistory(templateId);
    }
  };

  const handleRollback = async (templateId, version) => {
    if (!window.confirm(`Roll back ${templateId} to version ${version}?`)) return;
    setRollingBack(prev => ({ ...prev, [`${templateId}-${version}`]: true }));
    try {
      const note = rollbackNote[templateId] || `Rolled back to v${version}`;
      const res = await fetch(`${API_URL}/lesson/templates/${templateId}/deployment/rollback`, {
        method: 'POST', headers,
        body: JSON.stringify({ version, note }),
      });
      const data = await res.json();
      if (data.success) {
        setHistories(prev => { const n = { ...prev }; delete n[templateId]; return n; });
        fetchHistory(templateId);
        alert(data.warning
          ? `Rolled back to v${version}.\n\nNote: ${data.warning}`
          : `Successfully rolled back to v${version} and re-deployed.`
        );
      } else {
        alert(`Rollback failed: ${data.error}`);
      }
    } catch (e) {
      alert(`Rollback error: ${e}`);
    } finally {
      setRollingBack(prev => { const n = { ...prev }; delete n[`${templateId}-${version}`]; return n; });
    }
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  if (deployedLessons.length === 0) {
    return (
      <div style={{ ...styles.card, textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
        <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.2 }}>⬡</div>
        <div style={{ fontSize: '13px' }}>No deployed lessons yet.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 220px 100px 100px 36px',
        gap: '12px', padding: '4px 16px',
        fontSize: '10px', fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: '0.07em',
      }}>
        <span>Lesson</span>
        <span>Live URL</span>
        <span>Version</span>
        <span>Deployed</span>
        <span />
      </div>

      {deployedLessons.map(lesson => {
        const rawDep = deployments[lesson.id];
        const dep = normDep(rawDep);
        if (!dep) return null;

        const prog = progress[lesson.id];
        const isExpanded = expandedId === lesson.id;
        const history = histories[lesson.id] || [];
        const isLoadingHist = loadingHistory[lesson.id];
        const currentVersion = dep.version || '—';
        const totalVersions = rawDep?.history?.length || (rawDep?.current ? rawDep.history?.length : null);

        return (
          <div key={lesson.id} style={{
            ...styles.card,
            borderLeft: '3px solid #3b82f6',
            padding: 0, overflow: 'hidden',
          }}>
            {/* Main row */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 220px 100px 100px 36px',
              gap: '12px', alignItems: 'center', padding: '12px 16px',
            }}>
              {/* Lesson name + creator */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lesson.name || lesson.id}
                </div>
                {/* FIX: was #dce1e8 (near-white, hard to read on white bg elements); use muted slate */}
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>{lesson.id}</div>
                {prog && (
                  <div style={{ marginTop: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px' }}>
                      {prog.learner_count} learner{prog.learner_count !== 1 ? 's' : ''} · {prog.avg_percent}%
                    </div>
                    <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', width: '80px' }}>
                      <div style={{ height: '100%', width: `${prog.avg_percent}%`, background: '#a78bfa', borderRadius: '2px' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* URL + copy */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <a href={dep.deployment_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '11px', color: '#60a5fa', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {dep.deployment_url}
                </a>
              </div>

              {/* Version badge */}
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                  background: 'rgba(96,165,250,0.12)', color: '#60a5fa',
                  border: '1px solid rgba(96,165,250,0.25)',
                }}>
                  v{currentVersion}
                </span>
                {dep.deployed_by && (
                  // FIX: was #dce1e8 (near-white on dark → invisible); use readable muted slate
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px' }}>
                    by {getUserName(dep.deployed_by)}
                  </div>
                )}
              </div>

              {/* Deployed date — FIX: was #475569 (too dark); bump to #8892a4 for legibility */}
              <div style={{ fontSize: '11px', color: '#8892a4' }}>
                {fmtDate(dep.deployed_at)}
                {dep.note && (
                  // FIX: was #dce1e8; use consistent muted tone
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px', fontStyle: 'italic' }}>
                    "{dep.note}"
                  </div>
                )}
              </div>

              {/* Expand toggle — FIX: inactive was #475569 (near-invisible); bump to #8892a4 */}
              <button
                onClick={() => handleExpand(lesson.id)}
                style={{
                  width: '28px', height: '28px', borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: isExpanded ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.03)',
                  color: isExpanded ? '#60a5fa' : '#8892a4',
                  cursor: 'pointer', fontSize: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
                title="Version history"
              >
                {isExpanded ? '▲' : '▼'}
              </button>
            </div>

            {/* Version history panel */}
            {isExpanded && (
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.05)',
                background: 'rgba(0,0,0,0.25)',
                padding: '16px',
              }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#00ff9d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px', opacity: 0.7 }}>
                  Version History
                </div>

                {/* Rollback note input */}
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* FIX: was color: 'white' which is fine but let's keep consistent */}
                  <span style={{ fontSize: '10px', color: '#cbd5e1', flexShrink: 0 }}>Rollback note:</span>
                  <input
                    value={rollbackNote[lesson.id] || ''}
                    onChange={e => setRollbackNote(prev => ({ ...prev, [lesson.id]: e.target.value }))}
                    placeholder="Optional — reason for rollback"
                    style={{ ...styles.input, padding: '5px 10px', fontSize: '11px', flex: 1 }}
                  />
                </div>

                {/* FIX: was #dce1e8 for loading/empty states */}
                {isLoadingHist ? (
                  <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px 0' }}>Loading history...</div>
                ) : history.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>No history available.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {/* Column headers — FIX: was #64748b (too dark); bump to #8892a4 */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '50px 1fr 140px 120px 100px',
                      gap: '10px', padding: '0 8px',
                      fontSize: '12px', fontWeight: 700, color: '#8892a4',
                      textTransform: 'uppercase', letterSpacing: '0.07em',
                    }}>
                      <span>Ver</span><span>Note / URL</span><span>Deployed By</span><span>Date</span><span />
                    </div>

                    {history.map((entry) => {
                      const isCurrent = entry.version === dep.version;
                      const rollbackKey = `${lesson.id}-${entry.version}`;
                      const isRollingBackThis = rollingBack[rollbackKey];

                      return (
                        <div key={entry.version} style={{
                          display: 'grid', gridTemplateColumns: '50px 1fr 140px 120px 100px',
                          gap: '10px', alignItems: 'center',
                          padding: '8px', borderRadius: '8px',
                          background: isCurrent ? 'rgba(96,165,250,0.07)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${isCurrent ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.05)'}`,
                        }}>
                          {/* Version number */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              fontSize: '12px', fontWeight: 800,
                              // FIX: non-current was #475569 (near-invisible); bump to #8892a4
                              color: isCurrent ? '#60a5fa' : '#8892a4',
                            }}>
                              v{entry.version}
                            </span>
                            {isCurrent && (
                              <span style={{
                                fontSize: '8px', fontWeight: 700, color: '#60a5fa',
                                background: 'rgba(96,165,250,0.15)', padding: '1px 5px', borderRadius: '4px',
                              }}>LIVE</span>
                            )}
                          </div>

                          {/* Note + URL */}
                          <div style={{ minWidth: 0 }}>
                            {entry.note && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                "{entry.note}"
                              </div>
                            )}
                            <a href={entry.deployment_url} target="_blank" rel="noopener noreferrer"
                              // FIX: was #dce1e8 (near-white, invisible on light surfaces); use proper blue link
                              style={{ fontSize: '10px', color: '#60a5fa', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                              {entry.deployment_url}
                            </a>
                          </div>

                          {/* Deployed by — FIX: was #475569; bump to #8892a4 */}
                          <div style={{ fontSize: '11px', color: '#8892a4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getUserName(entry.deployed_by) || '—'}
                          </div>

                          {/* Date — FIX: was #475569; bump to #8892a4 */}
                          <div style={{ fontSize: '10px', color: '#8892a4' }}>
                            {fmtDateTime(entry.deployed_at)}
                          </div>

                          {/* Rollback button */}
                          <div>
                            {/* FIX: was #dce1e8 for "Current" label */}
                            {isCurrent ? (
                              <span style={{ fontSize: '10px', color: '#94a3b8' }}>Current</span>
                            ) : (
                              <button
                                onClick={() => handleRollback(lesson.id, entry.version)}
                                disabled={isRollingBackThis}
                                style={{
                                  padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: isRollingBackThis ? 'not-allowed' : 'pointer',
                                  fontSize: '11px', fontWeight: 600,
                                  background: isRollingBackThis ? 'rgba(251,191,36,0.07)' : 'rgba(251,191,36,0.1)',
                                  color: '#fbbf24',
                                  border: '1px solid rgba(251,191,36,0.2)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                {isRollingBackThis ? '...' : '↩ Rollback'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function ManagerDashboard({ token, users, API_URL }) {
  const [lessons, setLessons] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [reviewData, setReviewData] = useState({});
  const [deployments, setDeployments] = useState({});
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('lessons');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const tmplRes = await fetch(`${API_URL}/lesson/templates`, { headers });
        const tmplData = await tmplRes.json();
        const templates = tmplData.templates || [];
        setLessons(templates);

        const statusMap = {};
        const reviewMap = {};
        await Promise.all(templates.map(async t => {
          try {
            const res = await fetch(`${API_URL}/lesson/templates/${t.id}/review/status`, { headers });
            const d = await res.json();
            if (d.success) {
              statusMap[t.id] = d.review?.status || 'draft';
              reviewMap[t.id] = d.review || {};
            }
          } catch {
            statusMap[t.id] = 'draft';
            reviewMap[t.id] = {};
          }
        }));
        setStatuses(statusMap);
        setReviewData(reviewMap);

        try {
          const depRes = await fetch(`${API_URL}/admin/deployments`, { headers });
          if (depRes.ok) {
            const depData = await depRes.json();
            if (depData.success) setDeployments(depData.deployments || {});
          }
        } catch { }

        try {
          const progRes = await fetch(`${API_URL}/admin/progress-summary`, { headers });
          if (progRes.ok) {
            const progData = await progRes.json();
            if (progData.success) setProgress(progData.summary || {});
          }
        } catch { }

      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const getUserName = (userId) => {
    if (!userId) return null;
    const u = users.find(u => u.user_id === userId);
    return u ? (u.name || u.email?.split('@')[0]) : userId.slice(-6);
  };

  const getUserEmail = (userId) => {
    if (!userId) return null;
    const u = users.find(u => u.user_id === userId);
    return u?.email || null;
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso.slice(0, 10); }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso.slice(0, 16); }
  };

  const getAssignedUsers = (lesson) => {
    const raw = lesson.assigned_users || {};
    const flat = {};
    for (const [role, val] of Object.entries(raw)) {
      flat[role] = Array.isArray(val) ? val.filter(Boolean) : (val ? [val] : []);
    }
    return flat;
  };

  const userWorkload = React.useMemo(() => {
    const map = {};
    lessons.forEach(l => {
      const assigned = getAssignedUsers(l);
      const status = statuses[l.id] || 'draft';
      Object.entries(assigned).forEach(([role, uids]) => {
        uids.forEach(uid => {
          if (!map[uid]) map[uid] = [];
          map[uid].push({ lessonId: l.id, lessonName: l.name || l.id, role, status });
        });
      });
    });
    return map;
  }, [lessons, statuses]);

  const grouped = lessons.reduce((acc, l) => {
    const s = statuses[l.id] || 'draft';
    if (!acc[s]) acc[s] = [];
    acc[s].push(l);
    return acc;
  }, {});

  const deployedLessons = lessons.filter(l => deployments[l.id]);
  const activeUserCount = Object.keys(userWorkload).length;
  const allProgressVals = Object.values(progress).map(p => p.avg_percent || 0);
  const avgProgress = allProgressVals.length
    ? Math.round(allProgressVals.reduce((a, b) => a + b, 0) / allProgressVals.length)
    : null;

  const statusOrder = ['in_review', 'final_review', 'changes_requested', 'approved', 'deployed', 'draft'];

  const filteredLessons = filterStatus === 'all'
    ? statusOrder.flatMap(s => grouped[s] || [])
    : grouped[filterStatus] || [];

  const stats = [
    { label: 'Total Lessons', value: lessons.length, color: '#818cf8', icon: '◫' },
    { label: 'In Progress', value: (grouped['in_review'] || []).length + (grouped['final_review'] || []).length + (grouped['changes_requested'] || []).length, color: '#fbbf24', icon: '◉' },
    { label: 'Approved', value: (grouped['approved'] || []).length, color: '#34d399', icon: '◈' },
    { label: 'Deployed', value: (grouped['deployed'] || []).length + deployedLessons.length, color: '#60a5fa', icon: '⬡' },
  ];

  const tabBtn = (active) => ({
    padding: '6px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: 700, transition: 'all 0.15s',
    background: active ? 'rgba(0,255,157,0.12)' : 'transparent',
    color: active ? '#00ff9d' : '#8892a4',   // FIX: was #475569 (too dark for inactive tabs)
    borderBottom: active ? '2px solid #00ff9d' : '2px solid transparent',
  });

  const historyAction = (action) => {
    const map = {
      submitted_for_review: { label: 'Submitted for Review', color: '#fbbf24' },
      changes_requested: { label: 'Changes Requested', color: '#ef4444' },
      escalated_to_final: { label: 'Escalated to Final Review', color: '#8b5cf6' },
      approved: { label: 'Approved', color: '#10b981' },
      deployed: { label: 'Deployed', color: '#3b82f6' },
    };
    return map[action] || { label: action, color: '#94a3b8' };  // FIX: fallback was #64748b
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
        <div>
          <p style={styles.sectionTitle}>Manager Dashboard</p>
          <p style={styles.sectionSub}>Real-time overview of all lesson production activity</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{ ...styles.btn('ghost'), fontSize: '12px', padding: '6px 12px' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
        gap: '10px', marginBottom: '24px',
      }}>
        {stats.map(s => (
          <div key={s.label} style={{
            ...styles.card, textAlign: 'center', padding: '14px 10px',
            borderTop: `2px solid ${s.color}`,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '8px', right: '10px',
              fontSize: '18px', opacity: 0.1, color: s.color,
            }}>{s.icon}</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            {/* FIX: was #475569 (near-invisible); bump to #8892a4 */}
            <div style={{ fontSize: '10px', color: '#8892a4', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '20px' }}>
        {[
          { id: 'lessons', label: `All Lessons (${lessons.length})` },
          { id: 'team', label: `Team Workload (${activeUserCount})` },
          { id: 'deployed', label: `Deployed (${deployedLessons.length})` },
          { id: 'tracking', label: 'Tracking' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabBtn(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '13px' }}>
          <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.2 }}>◉</div>
          Loading dashboard...
        </div>
      ) : (
        <>
          {/* TAB: ALL LESSONS */}
          {activeTab === 'lessons' && (
            <div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <button onClick={() => setFilterStatus('all')} style={{
                  padding: '4px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                  fontSize: '11px', fontWeight: 700,
                  background: filterStatus === 'all' ? '#6366f1' : 'rgba(255,255,255,0.06)',
                  color: filterStatus === 'all' ? '#fff' : '#94a3b8',  // FIX: was #475569
                }}>All</button>
                {statusOrder.map(s => {
                  const count = (grouped[s] || []).length;
                  if (!count) return null;
                  const active = filterStatus === s;
                  return (
                    <button key={s} onClick={() => setFilterStatus(s)} style={{
                      padding: '4px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                      fontSize: '11px', fontWeight: 700,
                      background: active ? STATUS_COLORS[s] : STATUS_COLORS[s] + '18',
                      color: active ? '#fff' : STATUS_COLORS[s],
                    }}>
                      {STATUS_LABELS[s]} ({count})
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {filteredLessons.map(lesson => {
                  const status = statuses[lesson.id] || 'draft';
                  const color = STATUS_COLORS[status] || '#64748b';
                  const assigned = getAssignedUsers(lesson);
                  const review = reviewData[lesson.id] || {};
                  const dep = deployments[lesson.id];
                  const prog = progress[lesson.id];
                  const isExpanded = expandedLesson === lesson.id;
                  const lastHistory = (review.history || []).slice(-1)[0];

                  return (
                    <div key={lesson.id} style={{
                      ...styles.card,
                      borderLeft: `3px solid ${color}`,
                      padding: '0',
                      overflow: 'hidden',
                      transition: 'all 0.15s',
                    }}>
                      <div
                        onClick={() => setExpandedLesson(isExpanded ? null : lesson.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '12px 16px', cursor: 'pointer',
                        }}
                      >
                        {/* FIX: chevron was #dce1e8; use muted slate */}
                        <span style={{ fontSize: '10px', color: '#94a3b8', flexShrink: 0, transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>▶</span>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {lesson.name || lesson.id}
                          </div>
                          {/* FIX: was #dce1e8 */}
                          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>{lesson.id}</div>
                        </div>

                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {Object.entries(assigned).flatMap(([role, uids]) =>
                            uids.slice(0, 1).map(uid => {
                              const color2 = ROLE_COLORS[role] || '#6366f1';
                              const name = getUserName(uid);
                              return name ? (
                                <div key={role + uid} title={`${role}: ${name}`} style={{
                                  ...styles.avatar(color2), width: '24px', height: '24px', fontSize: '10px',
                                }}>
                                  {name.charAt(0).toUpperCase()}
                                </div>
                              ) : null;
                            })
                          )}
                        </div>

                        {prog && (
                          <div style={{ width: '60px', flexShrink: 0 }}>
                            {/* FIX: was #475569 */}
                            <div style={{ fontSize: '9px', color: '#8892a4', marginBottom: '2px', textAlign: 'right' }}>
                              {prog.learner_count}👤 {prog.avg_percent}%
                            </div>
                            <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                              <div style={{ height: '100%', width: `${prog.avg_percent}%`, background: '#a78bfa', borderRadius: '2px' }} />
                            </div>
                          </div>
                        )}

                        {dep && (
                          <a
                            href={dep.deployment_url} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: '11px', color: '#60a5fa', textDecoration: 'none', flexShrink: 0,
                              padding: '3px 8px', borderRadius: '6px', background: 'rgba(96,165,250,0.1)',
                              border: '1px solid rgba(96,165,250,0.2)', fontWeight: 600,
                            }}
                          >
                            🔗 Live
                          </a>
                        )}

                        <span style={styles.badge(color)}>{STATUS_LABELS[status]}</span>
                      </div>

                      {isExpanded && (
                        <div style={{
                          borderTop: '1px solid rgba(255,255,255,0.05)',
                          padding: '14px 16px 16px',
                          background: 'rgba(0,0,0,0.2)',
                          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
                        }}>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: '#00ff9d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', opacity: 0.7 }}>
                              Activity
                            </div>

                            {dep && (
                              <div style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.15)' }}>
                                <div style={{ fontSize: '10px', color: '#60a5fa', fontWeight: 700, marginBottom: '4px' }}>🌐 Deployed</div>
                                <a href={dep.deployment_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: '11px', color: '#93c5fd', wordBreak: 'break-all', textDecoration: 'none' }}>
                                  {dep.deployment_url}
                                </a>
                                {dep.deployed_at && (
                                  // FIX: was color: 'white' which is jarring; use softer tone
                                  <div style={{ fontSize: '10px', color: '#cbd5e1', marginTop: '3px' }}>{fmtDateTime(dep.deployed_at)}</div>
                                )}
                              </div>
                            )}

                            {(review.history || []).length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(review.history || []).slice(-3).reverse().map((h, i) => {
                                  const { label, color: hc } = historyAction(h.action);
                                  return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: hc, marginTop: '4px', flexShrink: 0 }} />
                                      <div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{label}</div>
                                        {h.by_user_id && (
                                          // FIX: was #dce1e8
                                          <div style={{ fontSize: '10px', color: '#94a3b8' }}>by {getUserName(h.by_user_id)}</div>
                                        )}
                                        {h.at && (
                                          // FIX: was color: 'white'
                                          <div style={{ fontSize: '10px', color: '#cbd5e1' }}>{fmtDateTime(h.at)}</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              // FIX: was #dce1e8
                              <div style={{ fontSize: '12px', color: '#94a3b8' }}>No activity yet</div>
                            )}

                            {prog && (
                              <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.15)' }}>
                                <div style={{ fontSize: '10px', color: '#a78bfa', fontWeight: 700, marginBottom: '6px' }}>📊 Learner Progress</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                                  <span>{prog.learner_count} learner{prog.learner_count !== 1 ? 's' : ''}</span>
                                  <span>{prog.avg_percent}% avg completion</span>
                                </div>
                                <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                                  <div style={{ height: '100%', width: `${prog.avg_percent}%`, background: '#a78bfa', borderRadius: '2px' }} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredLessons.length === 0 && (
                  // FIX: was #dce1e8
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }}>
                    No lessons in this category
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: TEAM WORKLOAD */}
          {activeTab === 'team' && (
            <div>
              {Object.keys(userWorkload).length === 0 ? (
                // FIX: was #dce1e8
                <div style={{ ...styles.card, textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.2 }}>◧</div>
                  <div style={{ fontSize: '13px' }}>No team assignments yet. Use Assignments or Backlogs to assign users.</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                  {Object.entries(userWorkload).map(([userId, tasks]) => {
                    const user = users.find(u => u.user_id === userId);
                    const name = getUserName(userId);
                    const email = getUserEmail(userId);
                    const roleSet = [...new Set(tasks.map(t => t.role))];
                    const statusCounts = tasks.reduce((acc, t) => {
                      acc[t.status] = (acc[t.status] || 0) + 1; return acc;
                    }, {});

                    return (
                      <div key={userId} style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                          <div style={styles.avatar('#6366f1')}>
                            {(name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#e2e8f0' }}>{name}</div>
                            {/* FIX: was #475569 */}
                            {email && <div style={{ fontSize: '10px', color: '#8892a4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#00ff9d', lineHeight: 1 }}>{tasks.length}</div>
                            {/* FIX: was #dce1e8 */}
                            <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase' }}>lessons</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
                          {roleSet.map(role => (
                            <span key={role} style={{ ...styles.badge(ROLE_COLORS[role] || '#6366f1'), fontSize: '10px' }}>
                              {role.replace('_', ' ')}
                            </span>
                          ))}
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          {/* FIX: was #dce1e8 */}
                          <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status Breakdown</div>
                          <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', gap: '1px' }}>
                            {Object.entries(statusCounts).map(([s, count]) => (
                              <div key={s} title={`${STATUS_LABELS[s] || s}: ${count}`} style={{
                                flex: count, background: STATUS_COLORS[s] || '#94a3b8',  // FIX: fallback was #dce1e8
                              }} />
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '5px' }}>
                            {Object.entries(statusCounts).map(([s, count]) => (
                              // FIX: fallback was #475569
                              <span key={s} style={{ fontSize: '10px', color: STATUS_COLORS[s] || '#8892a4' }}>
                                {STATUS_LABELS[s] || s} ({count})
                              </span>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                          {tasks.map((t, i) => {
                            const sc = STATUS_COLORS[t.status] || '#8892a4';  // FIX: fallback was #64748b
                            const rc = ROLE_COLORS[t.role] || '#6366f1';
                            return (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '5px 8px', borderRadius: '6px',
                                background: 'rgba(255,255,255,0.02)',
                                border: '1px solid rgba(255,255,255,0.05)',
                              }}>
                                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: rc, flexShrink: 0 }} />
                                <div style={{ flex: 1, fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {t.lessonName}
                                </div>
                                <span style={{ fontSize: '9px', color: sc, flexShrink: 0, fontWeight: 700 }}>
                                  {STATUS_LABELS[t.status] || t.status}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'deployed' && (
            <DeployedTab
              deployments={deployments}
              deployedLessons={deployedLessons}
              progress={progress}
              users={users}
              token={token}
              API_URL={API_URL}
            />
          )}
          {activeTab === 'tracking' && (
            <TrackingTab token={token} API_URL={API_URL} />
          )}
        </>
      )}
    </div>
  );
}


function TrackingTab({ token, API_URL }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [learners, setLearners] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/admin/tracking/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { if (d.success) setData(d.lessons || []); })
      .finally(() => setLoading(false));
  }, [token]);

  const loadLearners = async (lessonId) => {
    if (learners[lessonId]) return;
    const r = await fetch(`${API_URL}/admin/tracking/lesson/${lessonId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    if (d.success) setLearners(prev => ({ ...prev, [lessonId]: d.learners || [] }));
  };

  const handleExpand = (lessonId) => {
    if (expanded === lessonId) { setExpanded(null); return; }
    setExpanded(lessonId);
    loadLearners(lessonId);
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading tracking data...</div>
  );

  if (data.length === 0) return (
    <div style={{ ...styles.card, textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
      <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.2 }}>◉</div>
      <div style={{ fontSize: '13px' }}>No tracking data yet. Learners need to complete lessons first.</div>
    </div>
  );

  return (
    <div>
      <p style={styles.sectionTitle}>Learner Tracking</p>
      <p style={styles.sectionSub}>Completion and performance across all deployed lessons</p>

      {/* Summary header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 36px',
        gap: '12px', padding: '4px 16px',
        fontSize: '10px', fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px',
      }}>
        <span>Lesson</span>
        <span>Learners</span>
        <span>Avg Score</span>
        <span>Completed</span>
        <span>Avg Time</span>
        <span />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {data.map(lesson => {
          const isExp = expanded === lesson.lesson_id;
          const ll = learners[lesson.lesson_id] || [];
          return (
            <div key={lesson.lesson_id} style={{
              ...styles.card, padding: 0, overflow: 'hidden',
              borderLeft: '3px solid #a78bfa',
            }}>
              {/* Row */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 36px',
                gap: '12px', alignItems: 'center', padding: '12px 16px',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0' }}>
                    {lesson.lesson_title || lesson.lesson_id}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>{lesson.lesson_id}</div>
                </div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa', textAlign: 'center' }}>
                  {lesson.learner_count}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#34d399', textAlign: 'center' }}>
                  {lesson.avg_score != null ? `${lesson.avg_score}%` : '—'}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#60a5fa' }}>
                    {lesson.completion_rate != null ? `${lesson.completion_rate}%` : '—'}
                  </div>
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', marginTop: '3px' }}>
                    <div style={{ height: '100%', width: `${lesson.completion_rate || 0}%`, background: '#60a5fa', borderRadius: '2px' }} />
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                  {lesson.avg_time_minutes != null ? `${lesson.avg_time_minutes}m` : '—'}
                </div>
                <button onClick={() => handleExpand(lesson.lesson_id)} style={{
                  width: '28px', height: '28px', borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: isExp ? 'rgba(167,139,250,0.1)' : 'rgba(255,255,255,0.03)',
                  color: isExp ? '#a78bfa' : '#8892a4',
                  cursor: 'pointer', fontSize: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isExp ? '▲' : '▼'}
                </button>
              </div>

              {/* Learner drill-down */}
              {isExp && (
                <div style={{
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  background: 'rgba(0,0,0,0.25)', padding: '16px',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', opacity: 0.7 }}>
                    Learner Breakdown
                  </div>
                  {ll.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>Loading...</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {/* Learner column headers */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 100px',
                        gap: '10px', padding: '0 8px',
                        fontSize: '10px', fontWeight: 700, color: '#8892a4',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px',
                      }}>
                        <span>Learner</span>
                        <span>Score</span>
                        <span>Completed</span>
                        <span>Time</span>
                        <span>Last Seen</span>
                      </div>
                      {ll.map((l, i) => (
                        <div key={i} style={{
                          display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 100px',
                          gap: '10px', alignItems: 'center',
                          padding: '7px 8px', borderRadius: '7px',
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#e2e8f0' }}>{l.name || '—'}</div>
                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>{l.email}</div>
                          </div>
                          <div style={{ fontSize: '12px', color: '#34d399', textAlign: 'center', fontWeight: 600 }}>
                            {l.score != null ? `${l.score}%` : '—'}
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            {l.completed
                              ? <span style={{ fontSize: '11px', color: '#34d399', fontWeight: 700 }}>✓</span>
                              : <span style={{ fontSize: '11px', color: '#94a3b8' }}>—</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
                            {l.time_minutes != null ? `${l.time_minutes}m` : '—'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#8892a4' }}>
                            {l.last_seen ? new Date(l.last_seen).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Custom dark-themed dropdown (replaces native <select> which renders white) ─
function DarkSelect({ options, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '9px 12px',
        background: 'rgba(0,255,157,0.04)', border: '1px solid rgba(0,255,157,0.15)',
        borderRadius: '8px', color: '#64748b', fontSize: '13px', cursor: 'pointer',
        boxSizing: 'border-box', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', userSelect: 'none',
      }}>
        <span>{placeholder}</span>
        <span style={{ fontSize: '10px', color: '#475569' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#0d1b2a', border: '1px solid rgba(0,255,157,0.2)',
          borderRadius: '8px', zIndex: 200,
          maxHeight: '220px', overflowY: 'auto',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.6)',
        }}>
          {options.length === 0 ? (
            <div style={{ padding: '10px 14px', color: '#475569', fontSize: '12px' }}>No options available</div>
          ) : options.map(opt => (
            <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{ padding: '9px 14px', color: '#e2e8f0', fontSize: '13px', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,255,157,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{opt.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Organizations Section ─────────────────────────────────────────────────────
function OrganizationsSection({ token, users, API_URL }) {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchOrgs = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/organizations`, { headers });
      const data = await res.json();
      if (data.success) setOrgs(data.organizations || []);
    } catch (e) { console.warn('Failed to load orgs', e); }
  };

  useEffect(() => { fetchOrgs(); }, [token]);

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/organizations`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: newOrgName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setNewOrgName(''); setCreating(false);
        await fetchOrgs();
        setSelectedOrg(data.org);
      } else {
        setError(data.detail || 'Failed to create organization');
      }
    } catch (e) { setError('Failed to create organization'); }
    finally { setSaving(false); }
  };

  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm('Delete this organization? All member assignments will be removed.')) return;
    try {
      await fetch(`${API_URL}/admin/organizations/${orgId}`, { method: 'DELETE', headers });
      if (selectedOrg?.org_id === orgId) setSelectedOrg(null);
      await fetchOrgs();
    } catch (e) { setError('Failed to delete organization'); }
  };

  const handleAddMember = async (userId) => {
    if (!selectedOrg) return;
    try {
      await fetch(`${API_URL}/admin/organizations/${selectedOrg.org_id}/members`, {
        method: 'POST', headers, body: JSON.stringify({ user_id: userId }),
      });
      const updated = { ...selectedOrg, members: [...(selectedOrg.members || []), userId] };
      setSelectedOrg(updated);
      setOrgs(prev => prev.map(o => o.org_id === selectedOrg.org_id ? updated : o));
    } catch (e) { setError('Failed to add member'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedOrg) return;
    try {
      await fetch(`${API_URL}/admin/organizations/${selectedOrg.org_id}/members/${userId}`, {
        method: 'DELETE', headers,
      });
      const updated = { ...selectedOrg, members: (selectedOrg.members || []).filter(m => m !== userId), organizer_id: selectedOrg.organizer_id === userId ? null : selectedOrg.organizer_id };
      setSelectedOrg(updated);
      setOrgs(prev => prev.map(o => o.org_id === selectedOrg.org_id ? updated : o));
    } catch (e) { setError('Failed to remove member'); }
  };

  const handleSetOrganizer = async (userId) => {
    if (!selectedOrg) return;
    try {
      await fetch(`${API_URL}/admin/organizations/${selectedOrg.org_id}/organizer`, {
        method: 'POST', headers, body: JSON.stringify({ user_id: userId }),
      });
      const members = (selectedOrg.members || []);
      const updated = { ...selectedOrg, organizer_id: userId, members: members.includes(userId) ? members : [...members, userId] };
      setSelectedOrg(updated);
      setOrgs(prev => prev.map(o => o.org_id === selectedOrg.org_id ? updated : o));
    } catch (e) { setError('Failed to set organizer'); }
  };

  const getUserInfo = (userId) => users.find(u => u.user_id === userId);
  const nonMembers = users.filter(u => !(selectedOrg?.members || []).includes(u.user_id));
  const memberUsers = (selectedOrg?.members || []).map(uid => getUserInfo(uid)).filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <p style={styles.sectionTitle}>Organizations</p>
          <p style={styles.sectionSub}>Create isolated user groups. Each org has its own organizer who manages members and lesson assignments.</p>
        </div>
        <button onClick={() => setCreating(true)} style={styles.btn('primary')}>+ New Organization</button>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>}

      {creating && (
        <div style={{ ...styles.card, marginBottom: '20px', borderColor: 'rgba(249,115,22,0.3)' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#e2e8f0', marginBottom: '16px' }}>New Organization</div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <span style={styles.label}>Organization Name</span>
              <input value={newOrgName} onChange={e => setNewOrgName(e.target.value)}
                placeholder="e.g. Acme Corp" style={styles.input}
                onFocus={e => e.target.style.borderColor = 'rgba(249,115,22,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,255,157,0.15)'}
                onKeyDown={e => e.key === 'Enter' && handleCreateOrg()} />
            </div>
            <button onClick={handleCreateOrg} disabled={saving || !newOrgName.trim()} style={{ ...styles.btn('primary'), whiteSpace: 'nowrap' }}>
              {saving ? 'Creating...' : 'Create'}
            </button>
            <button onClick={() => { setCreating(false); setNewOrgName(''); }} style={styles.btn('ghost')}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Left: org list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {orgs.length === 0 ? (
            <div style={{ ...styles.card, textAlign: 'center', padding: '40px', color: '#475569' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>◬</div>
              <div>No organizations yet. Create one to get started.</div>
            </div>
          ) : (
            orgs.map(org => {
              const isSelected = selectedOrg?.org_id === org.org_id;
              return (
                <div key={org.org_id} onClick={() => setSelectedOrg(org)} style={{
                  ...styles.listItem(isSelected),
                  borderLeft: `3px solid ${isSelected ? '#f97316' : 'rgba(249,115,22,0.2)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>◬</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#e2e8f0' }}>{org.name}</div>
                      <div style={{ fontSize: '11px', color: '#475569' }}>{(org.members || []).length} members · {org.organizer_id ? `Organizer assigned` : 'No organizer'}</div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleDeleteOrg(org.org_id); }}
                      style={{ ...styles.btn('danger'), fontSize: '11px', padding: '4px 10px' }}>Delete</button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right: org detail */}
        <div style={{ width: '340px', flexShrink: 0 }}>
          {selectedOrg ? (
            <div style={styles.card}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#e2e8f0', marginBottom: '4px' }}>{selectedOrg.name}</div>
              <div style={{ fontSize: '11px', color: '#475569', marginBottom: '20px' }}>ID: {selectedOrg.org_id}</div>

              {/* Organizer */}
              <span style={styles.label}>Organizer</span>
              <div style={{ marginBottom: '16px' }}>
                {selectedOrg.organizer_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)', marginBottom: '6px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: 'rgba(249,115,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#f97316' }}>
                      {(getUserInfo(selectedOrg.organizer_id)?.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#f97316' }}>{getUserInfo(selectedOrg.organizer_id)?.email || selectedOrg.organizer_id}</div>
                      <div style={{ fontSize: '10px', color: '#475569' }}>Organizer</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#475569', marginBottom: '6px' }}>No organizer assigned yet</div>
                )}
                <div>
                  <span style={{ ...styles.label, marginBottom: '4px' }}>Set organizer from members</span>
                  <DarkSelect
                    placeholder="Select a member to make organizer…"
                    options={memberUsers.map(u => ({ value: u.user_id, label: u.email }))}
                    onChange={handleSetOrganizer}
                  />
                </div>
              </div>

              {/* Members */}
              <span style={styles.label}>Members ({(selectedOrg.members || []).length})</span>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px', background: 'rgba(2,6,23,0.6)', borderRadius: '8px', padding: '4px 0' }}>
                {memberUsers.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#475569', padding: '8px 12px' }}>No members yet</div>
                ) : (
                  memberUsers.map(u => (
                    <div key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(0,255,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#00ff9d', flexShrink: 0 }}>
                        {(u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                        {selectedOrg.organizer_id === u.user_id && <div style={{ fontSize: '10px', color: '#f97316' }}>Organizer</div>}
                      </div>
                      <button onClick={() => handleRemoveMember(u.user_id)}
                        style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}
                        title="Remove from org">✕</button>
                    </div>
                  ))
                )}
              </div>

              {/* Add member */}
              <span style={styles.label}>Add member</span>
              <DarkSelect
                placeholder="Select user to add…"
                options={nonMembers.map(u => ({ value: u.user_id, label: u.email }))}
                onChange={handleAddMember}
              />
            </div>
          ) : (
            <div style={{ ...styles.card, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <div style={{ textAlign: 'center', color: '#475569', fontSize: '13px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>◬</div>
                Select an organization to manage
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
